import {useCallback, useMemo, useRef, useSyncExternalStore} from 'react';
import type {LoadStatus} from 'api/types';

export type SetLoadStatus = (id: string, status: LoadStatus) => void;

type Store = {
  setStatus: SetLoadStatus;
  subscribe: (listener: () => void) => () => void;
  getAggregate: (ids: ReadonlyArray<string>) => LoadStatus;
};

const createStore = (): Store => {
  const statuses = new Map<string, LoadStatus>();
  const listeners = new Set<() => void>();
  let notifyScheduled = false;

  const notify = () => {
    notifyScheduled = false;
    listeners.forEach(l => l());
  };

  return {
    setStatus: (id, status) => {
      if (statuses.get(id) === status) {
        return;
      }
      statuses.set(id, status);
      if (!notifyScheduled) {
        notifyScheduled = true;
        queueMicrotask(notify);
      }
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getAggregate: ids => {
      if (ids.length === 0) {
        return 'Loaded';
      }
      let allLoaded = true;
      for (const id of ids) {
        const s = statuses.get(id);
        if (s === 'Failed') {
          return 'Failed';
        }
        if (s !== 'Loaded') {
          allLoaded = false;
        }
      }
      return allLoaded ? 'Loaded' : 'Loading';
    },
  };
};

/**
 * Aggregates per-id load statuses reported by child components into a single
 * `LoadStatus` for the parent, without queueing one parent setState per child.
 *
 * Children call the returned `setStatus(id, status)` — writes are coalesced
 * via a single microtask-scheduled notification, so N reports in the same
 * tick produce at most one parent re-render. The parent reads via
 * `useSyncExternalStore`, which short-circuits when the aggregate string is
 * unchanged (`Object.is`).
 */
export const useBatchedLoadStatus = (
  ids: ReadonlyArray<string>,
): {status: LoadStatus; setStatus: SetLoadStatus} => {
  const storeRef = useRef<Store | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = createStore();
  }
  const store = storeRef.current;

  const getSnapshot = useCallback(
    () => store.getAggregate(ids),
    [ids, store],
  );

  const status = useSyncExternalStore(store.subscribe, getSnapshot);

  return useMemo(
    () => ({status, setStatus: store.setStatus}),
    [status, store],
  );
};
